import { connect } from "react-redux";

import type {
  RelatedPublicTrip,
  RelatedPublicSegment,
  PublicTrip,
  RelatedPublicSegmentV4,
  RelatedPublicTripV4,
  PublicTripV4,
  PublicSegment,
  PublicSegmentV4
} from "@busbud/core-types/dist/lib/poll-service/api";

import { Itinerary as ItineraryComponent } from "@app/components/itinerary/itinerary";
import type { NormalizedTripStopAndTransfers } from "@app/components/itinerary/types";
import { getTripLegHasUnreliableDuration } from "@app/helpers/udm/get-leg-has-unreliable-duration";
import { getSegmentIdsPerLeg } from "@app/helpers/udm/get-segment-ids-per-leg";
import {
  tripStopsGenerationFromPublicSegments,
  tripStopsGenerationFromRelatedModels
} from "@app/helpers/udm/trip-stops-generation";
import type { Direction } from "@app/modules/results/components/departure/seat-selection-details/seat-selector/seat-selector";
import {
  getTripsCities,
  getTripsLocations,
  getTripsOperators,
  getTripsSegments,
  getTripStopsFetchStatus
} from "@app/modules/results/selectors/state-accessors";
import { getTripById } from "@app/modules/results/selectors/trips/get-trip";
import type { TripStopsFetchStatusKey } from "@app/modules/results/state/trip-stops-fetch-status";
import type { ResultsReduxState } from "@app/types/results-redux-types";

import { getCartSegmentsObject } from "../selectors/cart/get-cart-segments";
import { getCartTripItem } from "../selectors/cart/get-cart-trip-item";
import { getCartTripItemLegsSegmentIds } from "../selectors/cart/get-cart-trip-item-legs";
import {
  ASYNC_CART_VIEWS,
  getAsyncCartView
} from "../selectors/get-async-cart-view";

type OwnProps = {
  trip_id: string;
  direction: Direction;
};

export type StateProps = {
  direction: Direction;
  hide_time?: boolean;
  segments: (
    | RelatedPublicSegment
    | RelatedPublicSegmentV4
    | PublicSegment
    | PublicSegmentV4
  )[];
  trip_info:
    | RelatedPublicTrip
    | PublicTrip
    | RelatedPublicTripV4
    | PublicTripV4;
  trip_stops_and_transfers: NormalizedTripStopAndTransfers;
  trip_stops_fetch_status: TripStopsFetchStatusKey;
};

const mapCartStateToProps = (
  state: ResultsReduxState,
  own_props: OwnProps
): StateProps => {
  const { direction } = own_props;

  const trip_info = getCartTripItem(state);
  const all_segments = getCartSegmentsObject(state);
  const segment_ids_per_leg_matrix = getCartTripItemLegsSegmentIds(state);
  const segment_ids_per_leg =
    direction === "outbound"
      ? segment_ids_per_leg_matrix[0]
      : segment_ids_per_leg_matrix[1];

  const trip_segments = segment_ids_per_leg?.map(
    segment_id => all_segments[segment_id]
  );

  const trip_stops_and_transfers = tripStopsGenerationFromPublicSegments(
    trip_segments as unknown as PublicSegment[]
  );
  const trip_stops_fetch_status = getTripStopsFetchStatus(state);
  // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const hide_time = getTripLegHasUnreliableDuration(trip_segments);

  return {
    direction,
    hide_time,
    // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    segments: trip_segments,
    // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    trip_info,
    trip_stops_and_transfers,
    trip_stops_fetch_status
  };
};

const mapTripStateToProps = (
  state: ResultsReduxState,
  own_props: OwnProps
): StateProps => {
  const { trip_id, direction } = own_props;

  const trip_info = getTripById(state, trip_id);
  const all_segments = getTripsSegments(state);

  const trip_locations = getTripsLocations(state);
  const trip_operators = getTripsOperators(state);
  const trip_cities = getTripsCities(state);

  // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const segment_ids_per_leg_matrix = getSegmentIdsPerLeg(trip_info?.trip_legs);
  const segment_ids_per_leg =
    direction === "outbound"
      ? segment_ids_per_leg_matrix[0]
      : segment_ids_per_leg_matrix[1];

  const trip_segments = segment_ids_per_leg?.map(
    segment_id => all_segments[segment_id]
  );

  // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const hide_time = getTripLegHasUnreliableDuration(trip_segments);
  const trip_stops_and_transfers = tripStopsGenerationFromRelatedModels(
    // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    trip_segments,
    trip_locations,
    trip_operators,
    trip_cities
  );

  const trip_stops_fetch_status = getTripStopsFetchStatus(state);

  return {
    direction,
    hide_time,
    // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    segments: trip_segments,
    // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    trip_info,
    trip_stops_and_transfers,
    trip_stops_fetch_status
  };
};

const mapStateToProps = () => {
  return (state: ResultsReduxState, own_props: OwnProps): StateProps => {
    const { trip_id } = own_props;

    const async_cart_view = getAsyncCartView(state, trip_id);

    const itinerary =
      async_cart_view === ASYNC_CART_VIEWS.FULL_FORM_CART_SOURCE
        ? mapCartStateToProps(state, own_props)
        : mapTripStateToProps(state, own_props);

    return itinerary;
  };
};

const connector = connect(mapStateToProps);
export const AsyncCartItinerary = connector(ItineraryComponent);
